import React, { useState, useEffect } from "react";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import Swal from "sweetalert2";
import axios from "axios";
import "./AddTenantSecuritySettings.css";
import { API_BASE } from "../../../ApiUrl";
import SettingsTabs from "../Settings/SettingsTabs";

const SETTINGS_API_URL = `${API_BASE}/tenant-security-settings/`;
const SETTINGS_COMBINED_API_URL = `${API_BASE}/tenant-security-settings/combined/`;
const RESTRICTIONS_API_URL = `${API_BASE}/tenant-ip-restrictions/`;

const MFA_POLICY_OPTIONS = [
  { value: "OPTIONAL", label: "Optional" },
  { value: "REQUIRED_FOR_ADMINS", label: "Required for Admins" },
  { value: "REQUIRED_FOR_ALL", label: "Required for All" }
];

const SSO_STATUS_OPTIONS = [
  { value: "DISABLED", label: "Disabled" },
  { value: "CONFIGURING", label: "Configuring" },
  { value: "ACTIVE", label: "Active" },
  { value: "SUSPENDED", label: "Suspended" }
];

const API_ACCESS_STATUS_OPTIONS = [
  { value: "DISABLED", label: "Disabled" },
  { value: "SANDBOX", label: "Sandbox" },
  { value: "PRODUCTION", label: "Production" }
];

const emptyRestriction = { id: null, cidr_range: "" };

const emptySettings = {
  id: null,
  mfa_policy: "OPTIONAL",
  sso_status: "DISABLED",
  identity_provider: "",
  session_policy: "{}",
  api_access_status: "DISABLED",
  export_policy: "{}",
  ip_restrictions: [{ ...emptyRestriction }]
};

// Convert JSON value → pretty string for textarea
const jsonToString = (val) => {
  if (val === null || val === undefined) return "{}";
  if (typeof val === "string") {
    const trimmed = val.trim();
    if (!trimmed) return "{}";
    try {
      const parsed = JSON.parse(trimmed);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return val;
    }
  }
  if (typeof val === "object") {
    try {
      return JSON.stringify(val, null, 2);
    } catch {
      return "{}";
    }
  }
  return "{}";
};

const AddTenantSecuritySettings = () => {
  const [formData, setFormData] = useState({ ...emptySettings });
  const [savedData, setSavedData] = useState({ ...emptySettings });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(true);
  const [removedRestrictionIds, setRemovedRestrictionIds] = useState([]);

  const getTenantUser = () => {
    try {
      const tenantUser = localStorage.getItem("tenant_user");
      return tenantUser ? JSON.parse(tenantUser) : null;
    } catch (error) {
      console.error("Error reading tenant_user:", error);
      return null;
    }
  };

  // ---- Fetch existing security settings + nested IP restrictions ----
  useEffect(() => {
    const fetchExisting = async () => {
      const tenantUser = getTenantUser();

      if (!tenantUser?.tenant_id) {
        setFormData({ ...emptySettings });
        setSavedData({ ...emptySettings });
        setLoadingExisting(false);
        return;
      }

      try {
        setLoadingExisting(true);

        // 1. Fetch settings
        const settingsRes = await axios.get(SETTINGS_API_URL, {
          params: { tenant: tenantUser.tenant_id }
        });

        const rawSettings = settingsRes.data?.data
          ? settingsRes.data.data
          : Array.isArray(settingsRes.data)
          ? settingsRes.data
          : settingsRes.data?.results || [];

        const matched = rawSettings.find(
          (s) => String(s.tenant) === String(tenantUser.tenant_id)
        ) || rawSettings[0];

        // 2. Fetch restrictions
        let restrictionsForThis = [];
        if (matched?.id) {
          try {
            const restrictionsRes = await axios.get(RESTRICTIONS_API_URL, {
              params: { tenant: tenantUser.tenant_id }
            });

            const rawRestrictions = restrictionsRes.data?.data
              ? restrictionsRes.data.data
              : Array.isArray(restrictionsRes.data)
              ? restrictionsRes.data
              : restrictionsRes.data?.results || [];

            restrictionsForThis = rawRestrictions
              .filter(
                (r) => String(r.security_settings) === String(matched.id)
              )
              .map((r) => ({
                id: r.id,
                cidr_range: r.cidr_range || ""
              }));
          } catch (err) {
            console.warn("Could not load IP restrictions:", err);
          }
        }

        const loaded = matched
          ? {
              id: matched.id,
              mfa_policy: matched.mfa_policy || "OPTIONAL",
              sso_status: matched.sso_status || "DISABLED",
              identity_provider: matched.identity_provider || "",
              session_policy: jsonToString(matched.session_policy),
              api_access_status: matched.api_access_status || "DISABLED",
              export_policy: jsonToString(matched.export_policy),
              ip_restrictions: restrictionsForThis.length
                ? restrictionsForThis
                : [{ ...emptyRestriction }]
            }
          : { ...emptySettings };

        setFormData(loaded);
        setSavedData(loaded);
        setRemovedRestrictionIds([]);
      } catch (error) {
        console.error("Error fetching security settings:", error);
        Swal.fire({
          icon: "error",
          title: "Unable to Load Security Settings",
          text:
            error.response?.data?.detail ||
            error.response?.data?.message ||
            "Could not fetch existing security settings."
        });
        setFormData({ ...emptySettings });
        setSavedData({ ...emptySettings });
      } finally {
        setLoadingExisting(false);
      }
    };

    fetchExisting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Settings-level change ----
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ---- Restriction-level handlers ----
  const handleRestrictionChange = (rIndex, e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      ip_restrictions: prev.ip_restrictions.map((r, j) =>
        j === rIndex ? { ...r, cidr_range: value } : r
      )
    }));
  };

  const addRestriction = () => {
    setFormData((prev) => ({
      ...prev,
      ip_restrictions: [...prev.ip_restrictions, { ...emptyRestriction }]
    }));
  };

  const removeRestriction = (rIndex) => {
    setFormData((prev) => {
      const r = prev.ip_restrictions[rIndex];
      if (r?.id) {
        setRemovedRestrictionIds((prevIds) =>
          prevIds.includes(r.id) ? prevIds : [...prevIds, r.id]
        );
      }
      const next = prev.ip_restrictions.filter((_, j) => j !== rIndex);
      return {
        ...prev,
        ip_restrictions: next.length ? next : [{ ...emptyRestriction }]
      };
    });
  };

  // ---- Edit toggles ----
  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setFormData({ ...savedData });
    setRemovedRestrictionIds([]);
    setIsEditing(false);
  };

  // ---- Helpers ----
  const parseJsonObject = (raw, fieldLabel) => {
    const trimmed = (raw || "").trim();
    if (!trimmed) return {};
    const parsed = JSON.parse(trimmed);
    if (typeof parsed !== "object" || Array.isArray(parsed) || parsed === null) {
      throw new Error(`${fieldLabel} must be a JSON object.`);
    }
    return parsed;
  };

  const isValidCidr = (value) => {
    const trimmed = value.trim();
    if (!trimmed) return false;

    const ipv4 = /^(\d{1,3}\.){3}\d{1,3}(\/([0-9]|[12][0-9]|3[0-2]))$/;
    const ipv4Host = /^(\d{1,3}\.){3}\d{1,3}$/;
    const ipv6 = /^([0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}(\/\d{1,3})?$/;

    if (ipv4.test(trimmed) || ipv4Host.test(trimmed) || ipv6.test(trimmed)) {
      if (ipv4Host.test(trimmed)) {
        return trimmed
          .split(".")
          .every((octet) => Number(octet) >= 0 && Number(octet) <= 255);
      }
      return true;
    }
    return false;
  };

  const extractError = (error) => {
    if (error.response?.data) {
      const d = error.response.data;
      if (typeof d === "string") return d;
      if (d.detail) return d.detail;
      if (d.message) return d.message;
      if (d.errors) {
        return Object.entries(d.errors)
          .map(
            ([field, errors]) =>
              `${field}: ${Array.isArray(errors) ? errors.join(", ") : errors}`
          )
          .join(" | ");
      }
      return JSON.stringify(d);
    }
    return error.message || "Unknown error";
  };

  const validateForm = () => {
    try {
      parseJsonObject(formData.session_policy, "Session Policy");
    } catch (err) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Session Policy JSON",
        text: "Session Policy must be a valid JSON object."
      });
      return false;
    }

    try {
      parseJsonObject(formData.export_policy, "Export Policy");
    } catch (err) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Export Policy JSON",
        text: "Export Policy must be a valid JSON object."
      });
      return false;
    }

    if (formData.identity_provider.length > 100) {
      Swal.fire({
        icon: "warning",
        title: "Identity Provider Too Long",
        text: "Identity Provider must be 100 characters or fewer."
      });
      return false;
    }

    for (let i = 0; i < formData.ip_restrictions.length; i++) {
      const r = formData.ip_restrictions[i];
      const isBlank = !r.cidr_range.trim() && !r.id;
      if (isBlank) continue;

      if (!r.cidr_range.trim()) {
        Swal.fire({
          icon: "warning",
          title: "Required Field Missing",
          text: `Please provide a CIDR range for IP Restriction ${i + 1}.`
        });
        return false;
      }

      if (r.cidr_range.trim().length > 100) {
        Swal.fire({
          icon: "warning",
          title: "CIDR Range Too Long",
          text: `CIDR range for IP Restriction ${i + 1} must be 100 characters or fewer.`
        });
        return false;
      }

      if (!isValidCidr(r.cidr_range)) {
        Swal.fire({
          icon: "warning",
          title: "Invalid CIDR Range",
          text: `CIDR range for IP Restriction ${i + 1} is invalid. Use IPv4 or IPv6 (e.g. 192.168.1.0/24).`
        });
        return false;
      }
    }

    return true;
  };

  // ---- Payload builder (nested ip_restrictions) ----
  const buildPayload = () => {
    const existing = formData.ip_restrictions.filter(
      (r) => r.id && r.cidr_range.trim()
    );

    const newOnes = formData.ip_restrictions.filter(
      (r) => !r.id && r.cidr_range.trim()
    );

    return {
      mfa_policy: formData.mfa_policy,
      sso_status: formData.sso_status,
      identity_provider: formData.identity_provider.trim(),
      session_policy: parseJsonObject(formData.session_policy, "Session Policy"),
      api_access_status: formData.api_access_status,
      export_policy: parseJsonObject(formData.export_policy, "Export Policy"),
      ip_restrictions: {
        updated: existing.map((r) => ({
          id: r.id,
          cidr_range: r.cidr_range.trim()
        })),
        deleted_ids: removedRestrictionIds,
        new: newOnes.map((r) => ({
          cidr_range: r.cidr_range.trim()
        }))
      }
    };
  };

  // ---- Submit ----
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isEditing) return;

    const tenantUser = getTenantUser();

    if (!tenantUser) {
      Swal.fire({
        icon: "error",
        title: "Session Information Missing",
        text: "Tenant user information was not found in local storage."
      });
      return;
    }

    if (!tenantUser.tenant_id) {
      Swal.fire({
        icon: "error",
        title: "Invalid Tenant User",
        text: "Tenant ID is missing from tenant_user."
      });
      return;
    }

    if (!validateForm()) return;

    const CONFIG = { headers: { "Content-Type": "application/json" } };
    const payload = buildPayload();

    console.log("Security Settings payload:", payload);

    try {
      setLoading(true);

      let newId = formData.id;

      if (formData.id) {
        // UPDATE — combined endpoint
        await axios.put(
          `${SETTINGS_COMBINED_API_URL}${formData.id}/`,
          payload,
          CONFIG
        );
      } else {
        // CREATE — combined endpoint (include tenant)
        const createPayload = {
          ...payload,
          tenant: tenantUser.tenant_id
        };
        const res = await axios.post(
          SETTINGS_COMBINED_API_URL,
          createPayload,
          CONFIG
        );
        newId =
          res.data?.id || res.data?.data?.id || res.data?.pk || null;
      }

      // Refresh from backend so we get fresh restriction ids
      const refreshed = { ...formData, id: newId || formData.id };
      setSavedData(refreshed);
      setIsEditing(false);
      setRemovedRestrictionIds([]);

      // Re-fetch to sync restriction ids
      const settingsRes = await axios.get(
        `${SETTINGS_API_URL}${newId || formData.id}/`
      );
      const s = settingsRes.data?.data || settingsRes.data || {};

      const restrictionsRes = await axios.get(RESTRICTIONS_API_URL, {
        params: { tenant: tenantUser.tenant_id }
      });
      const rawRestrictions = restrictionsRes.data?.data
        ? restrictionsRes.data.data
        : Array.isArray(restrictionsRes.data)
        ? restrictionsRes.data
        : restrictionsRes.data?.results || [];

      const restrictionsForThis = rawRestrictions
        .filter((r) => String(r.security_settings) === String(s.id))
        .map((r) => ({ id: r.id, cidr_range: r.cidr_range || "" }));

      const reloaded = {
        id: s.id,
        mfa_policy: s.mfa_policy || "OPTIONAL",
        sso_status: s.sso_status || "DISABLED",
        identity_provider: s.identity_provider || "",
        session_policy: jsonToString(s.session_policy),
        api_access_status: s.api_access_status || "DISABLED",
        export_policy: jsonToString(s.export_policy),
        ip_restrictions: restrictionsForThis.length
          ? restrictionsForThis
          : [{ ...emptyRestriction }]
      };

      setFormData(reloaded);
      setSavedData(reloaded);

      await Swal.fire({
        icon: "success",
        title: formData.id ? "Updated Successfully" : "Saved Successfully",
        confirmButtonText: "OK"
      });
    } catch (error) {
      console.error("Save Security Settings Error:", error);
      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: `Unable to save security settings. ${extractError(error)}`
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="tenant-security-page">
          <SettingsTabs />

          <div className="tenant-security-page-header">
            <div>
              <h2>Tenant Security Settings</h2>
              <p>
                Configure MFA, SSO, session, API access, and allowed IP
                ranges for the tenant.
              </p>
            </div>
          </div>

          {loadingExisting ? (
            <div className="tenant-security-loading">
              Loading security settings...
            </div>
          ) : (
            <form
              className="tenant-security-card"
              onSubmit={handleSubmit}
            >
              <div className="tenant-security-form-body">
                <div className="tenant-security-record">
                  <div className="tenant-security-record-header">
                    <h4>
                      {formData.id
                        ? "Security Settings"
                        : "New Security Settings"}
                    </h4>
                  </div>

                  <div className="tenant-security-form-grid">
                    <div className="tenant-security-form-group">
                      <label>MFA Policy</label>
                      <select
                        name="mfa_policy"
                        value={formData.mfa_policy}
                        onChange={handleChange}
                        disabled={!isEditing}
                      >
                        {MFA_POLICY_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="tenant-security-form-group">
                      <label>SSO Status</label>
                      <select
                        name="sso_status"
                        value={formData.sso_status}
                        onChange={handleChange}
                        disabled={!isEditing}
                      >
                        {SSO_STATUS_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="tenant-security-form-group">
                      <label>Identity Provider</label>
                      <input
                        type="text"
                        name="identity_provider"
                        value={formData.identity_provider}
                        onChange={handleChange}
                        disabled={!isEditing}
                        placeholder="e.g. Okta, Azure AD, Google"
                        maxLength={100}
                      />
                    </div>

                    <div className="tenant-security-form-group">
                      <label>API Access Status</label>
                      <select
                        name="api_access_status"
                        value={formData.api_access_status}
                        onChange={handleChange}
                        disabled={!isEditing}
                      >
                        {API_ACCESS_STATUS_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="tenant-security-form-group tenant-security-form-group--full">
                      <label>Session Policy (JSON)</label>
                      <textarea
                        name="session_policy"
                        value={formData.session_policy}
                        onChange={handleChange}
                        disabled={!isEditing}
                        placeholder='e.g. {"timeout_minutes": 30}'
                        rows={4}
                        spellCheck={false}
                      />
                    </div>

                    <div className="tenant-security-form-group tenant-security-form-group--full">
                      <label>Export Policy (JSON)</label>
                      <textarea
                        name="export_policy"
                        value={formData.export_policy}
                        onChange={handleChange}
                        disabled={!isEditing}
                        placeholder='e.g. {"allow_csv": true}'
                        rows={4}
                        spellCheck={false}
                      />
                    </div>
                  </div>

                  {/* ---- Nested IP Restrictions ---- */}
                  <div className="tenant-ip-subsection">
                    <div className="tenant-ip-subsection-header">
                      <h5>IP Restrictions</h5>
                      {isEditing && (
                        <button
                          type="button"
                          className="tenant-ip-add-btn"
                          onClick={addRestriction}
                        >
                          <span>+</span>
                          Add IP Restriction
                        </button>
                      )}
                    </div>

                    {formData.ip_restrictions.map((r, rIndex) => (
                      <div
                        className="tenant-ip-row"
                        key={r.id || `new-r-${rIndex}`}
                      >
                        <div className="tenant-ip-row-input">
                          <input
                            type="text"
                            value={r.cidr_range}
                            onChange={(e) =>
                              handleRestrictionChange(rIndex, e)
                            }
                            disabled={!isEditing}
                            placeholder="e.g. 192.168.1.0/24"
                            maxLength={100}
                          />
                        </div>

                        {isEditing && (
                          <button
                            type="button"
                            className="tenant-ip-remove-btn"
                            onClick={() => removeRestriction(rIndex)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="tenant-security-form-actions">
                {!isEditing ? (
                  <button
                    type="button"
                    className="tenant-security-save-btn"
                    onClick={handleEdit}
                  >
                    Edit
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      className="tenant-security-cancel-btn"
                      onClick={handleCancelEdit}
                      disabled={loading}
                    >
                      Cancel
                    </button>

                    <button
                      type="submit"
                      className="tenant-security-save-btn"
                      disabled={loading}
                    >
                      {loading ? "Saving..." : "Save Changes"}
                    </button>
                  </>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddTenantSecuritySettings;